import { useMutation } from 'react-query';

import { axios } from '@/lib/axios';
import { MutationConfig, queryClient } from '@/lib/react-query';

import { useToast } from '@/components/ui/use-toast';
import { label } from '@/utils/label';
import { Invitation } from '../types';
import { LabelGroup } from '@/features/label/types';

const QUERY_KEY = 'invitations';

export const deleteInvitation = ({ invitationId }: { invitationId: number }) => {
  return axios.delete(`/api/invitation/${invitationId}`);
};

type UseDeleteInvitationOptions = {
  config?: MutationConfig<typeof deleteInvitation>;
};

export const useDeleteInvitation = ({ config }: UseDeleteInvitationOptions) => {
  const { toast } = useToast();

  return useMutation({
    onMutate: async (deleted) => {
      await queryClient.cancelQueries([QUERY_KEY, deleted.invitationId]);

      const prev = queryClient.getQueryData<Invitation[]>([QUERY_KEY]);

      queryClient.setQueryData(
        [QUERY_KEY],
        prev?.filter((user) => user?.invitationId !== deleted.invitationId)
      );

      return { prev };
    },
    onError: (_, __, context: any) => {
      if (context?.prev) {
        queryClient.setQueryData([QUERY_KEY], context.prev);
      }
      toast({
        variant: 'destructive',
        title: label('something_went_wrong', LabelGroup.GLOBAL),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEY]);
      queryClient.refetchQueries([QUERY_KEY]);

      toast({
        variant: 'success',
        title: label('invitation_deleted', LabelGroup.ORGANIZER_ORGANIZATION_EDIT),
      });
    },
    ...config,
    mutationFn: ({ invitationId }) => deleteInvitation({ invitationId }),
  });
};
